import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, RefreshCwIcon, InfoIcon, ShieldCheckIcon, ExternalLinkIcon, CopyIcon, CheckIcon, GlobeIcon, UsersIcon, KeyIcon, MailIcon, EyeIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { emailNotificationService, EmailConfig } from '../../utils/emailNotifications';
import { useToast } from '../../contexts/ToastContext';
export function VerificationCodesConfig({
  isReadOnly = false
}: {
  isReadOnly?: boolean;
}) {
  const {
    showToast
  } = useToast();
  const [config, setConfig] = useState<EmailConfig>({
    publicKey: '',
    serviceId: '',
    templateId: '',
    enabled: true,
    senderName: 'Savidna'
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: booleaan;
    message: string;
  } | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [copiedVar, setCopiedVar] = useState<string | null>(null);
  // Template variables for verification codes
  const verificationVars = [{
    name: 'to_email',
    desc: 'Correo del usuario'
  }, {
    name: 'to_name',
    desc: 'Nombre del usuario'
  }, {
    name: 'from_name',
    desc: 'Nombre del remitente'
  }, {
    name: 'subject',
    desc: 'Asunto del correo'
  }, {
    name: 'message',
    desc: 'Código de verificación y mensaje'
  }];
  useEffect(() => {
    const loadConfig = async () => {
      const savedConfig = emailNotificationService.getConfig();
      if (savedConfig) {
        setConfig({
          ...savedConfig,
          enabled: true
        });
      }
      await emailNotificationService.loadFromSupabase();
      const freshConfig = emailNotificationService.getConfig();
      if (freshConfig) {
        setConfig({
          ...freshConfig,
          enabled: true
        });
      }
    };
    loadConfig();
  }, []);
  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');
    const result = await emailNotificationService.saveConfig({
      ...config,
      enabled: true
    });
    if (result.success && !result.error) {
      showToast({
        type: 'success',
        title: 'Configuración guardada',
        message: 'Los códigos de verificación se enviarán por correo.'
      });
      setSaveMessage('✅ Configuración guardada. Los códigos de verificación se enviarán por correo.');
    } else if (result.success && result.error) {
      setSaveMessage(`⚠️ ${result.error}`);
    } else {
      setSaveMessage('❌ Error al guardar la configuración');
    }
    setIsSaving(false);
    setTimeout(() => setSaveMessage(''), 5000);
  };
  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    await emailNotificationService.saveConfig({
      ...config,
      enabled: true
    });
    if (!config.publicKey || !config.serviceId || !config.templateId) {
      setTestResult({
        success: false,
        message: 'Completa todos los campos: Public Key, Service ID y Template ID.'
      });
      setIsTesting(false);
      return;
    }
    setTestResult({
      success: true,
      message: '✅ Configuración válida. Los códigos de verificación se enviarán automáticamente por correo cuando un paciente se registre.'
    });
    setIsTesting(false);
  };
  const handleCopyVar = (varName: string) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    setCopiedVar(varName);
    setTimeout(() => setCopiedVar(null), 1500);
  };
  const isConfigComplete = config.publicKey && config.serviceId && config.templateId;
  return <div className="space-y-6" data-id="element-5255">
      <div data-id="element-5256">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5257">
          <ShieldCheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-5258" />
          Códigos de Verificación por Email
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5259">
          Envía códigos de verificación de 6 dígitos a los pacientes cuando se
          registran
        </p>
      </div>

      {/* Status Card */}
      <Card className={`${isConfigComplete ? 'bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 border-purple-200 dark:border-purple-800' : 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800'}`} data-id="element-5260">
        <div className="flex items-center justify-between" data-id="element-5261">
          <div className="flex items-center gap-4" data-id="element-5262">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg ${isConfigComplete ? 'bg-purple-500 dark:bg-purple-600 shadow-purple-500/20' : 'bg-amber-500 dark:bg-amber-600 shadow-amber-500/20'}`} data-id="element-5263">
              <KeyIcon className="w-7 h-7 text-white" data-id="element-5264" />
            </div>
            <div data-id="element-5265">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-5266">
                {isConfigComplete ? '✅ Verificación Activa' : '⚠️ Configuración Pendiente'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-5267">
                {isConfigComplete ? 'Los códigos se envían automáticamente al correo del paciente' : 'Configura EmailJS para enviar códigos de verificación reales'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 rounded-full border border-gray-200 dark:border-gray-700" data-id="element-5268">
            <UsersIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" data-id="element-5269" />
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400" data-id="element-5270">
              Compartido
            </span>
          </div>
        </div>
      </Card>

      {/* How it works */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 text-sm" data-id="element-5271">
        <div className="flex items-start gap-3" data-id="element-5272">
          <InfoIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-5273" />
          <div data-id="element-5274">
            <p className="font-medium mb-1" data-id="element-5275">¿Cómo funciona?</p>
            <ul className="text-blue-600 dark:text-blue-400 space-y-1 text-xs" data-id="element-5276">
              <li data-id="element-5277">
                • Cuando un paciente se registra, se genera un código de 6
                dígitos
              </li>
              <li data-id="element-5278">
                • El código se envía automáticamente a su correo electrónico
              </li>
              <li data-id="element-5279">• El paciente ingresa el código para verificar su email</li>
              <li data-id="element-5280">• Los códigos expiran en 10 minutos por seguridad</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Same config notice */}
      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg text-purple-700 dark:text-purple-300 text-sm flex items-start gap-2" data-id="element-5281">
        <GlobeIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-5282" />
        <div data-id="element-5283">
          <p className="font-medium" data-id="element-5284">Usa la misma configuración de EmailJS</p>
          <p className="text-purple-600 dark:text-purple-400 mt-0.5" data-id="element-5285">
            Los códigos de verificación usan la misma configuración de EmailJS
            que las notificaciones de citas. Si ya configuraste EmailJS, ¡ya
            está listo!
          </p>
        </div>
      </div>

      {saveMessage && <div className={`p-3 rounded-lg border text-sm ${saveMessage.startsWith('✅') ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400' : saveMessage.startsWith('⚠️') ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'}`} data-id="element-5286">
          {saveMessage}
        </div>}

      {/* EmailJS Configuration */}
      <Card data-id="element-5287">
        <div className="space-y-4" data-id="element-5288">
          <div className="flex items-center justify-between" data-id="element-5289">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-5290">
              Credenciales de EmailJS
            </h3>
            <a href="https://dashboard.emailjs.com" target="_blank" rel="noopener noreferrer" className="text-sm text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1" data-id="element-5291">
              Abrir Dashboard
              <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-5292" />
            </a>
          </div>

          {isReadOnly && <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-blue-700 dark:text-blue-300 flex items-center gap-3 text-sm shadow-sm" data-id="element-5293">
              <EyeIcon className="w-5 h-5 flex-shrink-0" data-id="element-5294" />
              <span className="font-medium" data-id="element-5295">Solo lectura — No tienes permisos para editar esta sección.</span>
            </div>}
          <Input label="Public Key" value={config.publicKey} onChange={e => {
          if (!isReadOnly) setConfig({
            ...config,
            publicKey: e.target.value
          });
        }} disabled={isReadOnly} placeholder="Ej: aBcDeFgHiJkLmN" helperText="Dashboard → Account → Public Key" data-id="element-5296" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5297">
            <Input label="Service ID" value={config.serviceId} onChange={e => setConfig({
            ...config,
            serviceId: isReadOnly ? config.serviceId : e.target.value
          })} disabled={isReadOnly} placeholder="Ej: service_abc123" helperText="Dashboard → Email Services → Service ID" data-id="element-5298" />
            <Input label="Template ID" value={config.templateId} onChange={e => setConfig({
            ...config,
            templateId: isReadOnly ? config.templateId : e.target.value
          })} disabled={isReadOnly} placeholder="Ej: template_xyz789" helperText="Dashboard → Email Templates → Template ID" data-id="element-5299" />
          </div>

          <Input label="Nombre del Remitente" value={config.senderName} onChange={e => setConfig({
          ...config,
          senderName: isReadOnly ? config.senderName : e.target.value
        })} disabled={isReadOnly} placeholder="Savidna - Centro de Bienestar" data-id="element-5300" />

          <div className="flex items-center gap-3 pt-4 flex-wrap" data-id="element-5301">
            {!isReadOnly && <Button onClick={handleSave} isLoading={isSaving} data-id="element-5302">
              Guardar
            </Button>}
            <Button variant="secondary" onClick={handleTestConnection} disabled={isReadOnly} isLoading={isTesting} disabled={!isConfigComplete} data-id="element-5303">
              <RefreshCwIcon className="w-4 h-4" data-id="element-5304" />
              Verificar
            </Button>
            <Button variant="secondary" onClick={() => setShowInstructions(!showInstructions)} data-id="element-5305">
              <InfoIcon className="w-4 h-4" data-id="element-5306" />
              {showInstructions ? 'Ocultar' : 'Ver'} Instrucciones
            </Button>
          </div>

          {testResult && <div className={`p-3 rounded-lg border flex items-start gap-2 ${testResult.success ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'}`} data-id="element-5307">
              {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-5308" /> : <XCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-5309" />}
              <p className="font-medium whitespace-pre-line" data-id="element-5310">
                {testResult.message}
              </p>
            </div>}
        </div>
      </Card>

      {/* Instructions */}
      {showInstructions && <Card data-id="element-5311">
          <div className="space-y-6" data-id="element-5312">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-5313">
              Configuración Rápida
            </h3>

            <div className="space-y-6" data-id="element-5314">
              {/* Step 1 */}
              <div className="flex gap-4" data-id="element-5315">
                <div className="flex-shrink-0" data-id="element-5316">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-5317">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-5318">
                      1
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-5319">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-5320">
                    Crea una cuenta en EmailJS
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-5321">
                    Ve a{' '}
                    <a href="https://www.emailjs.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline" data-id="element-5322">
                      emailjs.com
                    </a>{' '}
                    y regístrate gratis (200 correos/mes).
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4" data-id="element-5323">
                <div className="flex-shrink-0" data-id="element-5324">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-5325">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-5326">
                      2
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-5327">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-5328">
                    Conecta tu Gmail
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-5329">
                    Dashboard → Email Services → Add New Service → Gmail. Copia
                    el <strong data-id="element-5330">Service ID</strong>.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4" data-id="element-5331">
                <div className="flex-shrink-0" data-id="element-5332">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-5333">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-5334">
                      3
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-5335">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-5336">
                    Crea o usa tu plantilla existente
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-5337">
                    Si ya tienes una plantilla para notificaciones de citas,{' '}
                    <strong data-id="element-5338">¡úsala!</strong> Los códigos de verificación usan la
                    misma plantilla. Solo asegúrate de incluir la variable{' '}
                    <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded" data-id="element-5339">
                      {'{{message}}'}
                    </code>
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-5340">
                    <div className="grid grid-cols-1 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5341">
                      {verificationVars.map(v => <div key={v.name} className="flex items-center justify-between px-3 py-2" data-id="element-5342">
                          <div className="flex items-center gap-2" data-id="element-5343">
                            <code className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded font-mono" data-id="element-5344">
                              {`{{${v.name}}}`}
                            </code>
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5345">
                              {v.desc}
                            </span>
                          </div>
                          <button onClick={() => handleCopyVar(v.name)} className="p-1 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 rounded transition-colors" title="Copiar" data-id="element-5346">
                            {copiedVar === v.name ? <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-5347" /> : <CopyIcon className="w-3.5 h-3.5" data-id="element-5348" />}
                          </button>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4" data-id="element-5349">
                <div className="flex-shrink-0" data-id="element-5350">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-5351">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-5352">
                      4
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-5353">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-5354">
                    Pega las credenciales aquí
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-5355">
                    Copia el Public Key, Service ID y Template ID de EmailJS y
                    pégalos arriba. Haz clic en Guardar y ¡listo!
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4" data-id="element-5356">
              <p className="text-sm text-purple-700 dark:text-purple-300" data-id="element-5357">
                <strong data-id="element-5358">✅ ¡Listo!</strong> Los códigos de verificación se
                enviarán automáticamente al correo del paciente cuando se
                registre. El código expira en 10 minutos.
              </p>
            </div>
          </div>
        </Card>}

      {/* Example email preview */}
      <Card data-id="element-5359">
        <div className="space-y-3" data-id="element-5360">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5361">
            <MailIcon className="w-5 h-5 text-purple-600" data-id="element-5362" />
            Vista Previa del Correo
          </h3>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4" data-id="element-5363">
            <div className="space-y-2 text-sm" data-id="element-5364">
              <p className="font-semibold text-gray-900 dark:text-white" data-id="element-5365">
                Asunto: 🔐 Código de Verificación - {config.senderName}
              </p>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 text-gray-600 dark:text-gray-400" data-id="element-5366">
                <p data-id="element-5367">Hola,</p>
                <p className="mt-2" data-id="element-5368">Tu código de verificación es:</p>
                <p className="my-3 text-2xl font-bold text-purple-600 dark:text-purple-400" data-id="element-5369">
                  123456
                </p>
                <p className="text-xs text-gray-500" data-id="element-5370">
                  Este código expira en 10 minutos.
                </p>
                <p className="mt-3 text-xs text-gray-500" data-id="element-5371">
                  Si no solicitaste este código, ignora este correo.
                </p>
                <p className="mt-4 text-xs" data-id="element-5372">
                  ---
                  <br data-id="element-5373" />
                  Saludos,
                  <br data-id="element-5374" />
                  {config.senderName}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
}